/**
 * Fixes UTF-8 mojibake — the garbled characters that appear when a UTF-8
 * encoded string (e.g. curly quotes: “ / “) is mistakenly decoded
 * as Windows-1252 / Latin-1, turning each original byte into a Latin-1
 * codepoint.
 *
 * Strategy:
 *  1. If value is not a string (null, undefined, number, etc.) return as-is.
 *  2. If empty, return as-is.
 *  3. If any codepoint is above U+00FF the string is already proper Unicode —
 *     return it unchanged.
 *  4. Otherwise reinterpret every codepoint as a raw byte and run it through
 *     the browser's TextDecoder as UTF-8 (fatal mode).
 *  5. If decoding succeeds the string was mojibake and is now fixed.
 *     If it throws the bytes are not valid UTF-8 (genuine Latin-1 content),
 *     so we return the original string untouched.
 */
function fixLocaleEncoding(value: string): string {
  if (typeof value !== 'string') {
    return value;
  }

  if (value.length === 0) {
    return value;
  }

  for (let i = 0; i < value.length; i += 1) {
    if (value.charCodeAt(i) > 0xff) {
      return value;
    }
  }

  try {
    const bytes = Uint8Array.from(value, (c) => c.charCodeAt(0));

    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return value;
  }
}

export default fixLocaleEncoding;
